import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { TabLayout } from "@/components/Layout/BaseLayouts/TabLayout";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { NewGroupCard } from "@/components/Portal/FriendsWithBenefits/Cards/NewGroupCard.tsx";
import { FriendsWithBenefitsHeader } from "@/components/Portal/FriendsWithBenefits/FriendsWithBenefitsHeader.tsx";
import { useAlert } from "@/hooks/useAlert";
import { useHandleError } from "@/hooks/useHandleError";
import { ErrorType } from "@/mutator/frontendApiInstance";
import { FullScreenLoadingContext } from "@/provider/FullScreenLoadingProvider/FullScreenLoadingProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useGetCustomer,
  useGetCustomerTribe,
  usePostTribe,
} from "@/services/api";
import { AlertTypes } from "@/utils/atoms";

const CreateGroup = () => {
  const { t } = useTranslation("portal", { keyPrefix: "friendsWithBenefits" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const showAlert = useAlert();
  const handleError = useHandleError();
  const { setFullScreenLoading } = useContext(FullScreenLoadingContext);
  const { data: customer } = useGetCustomer();
  const { data: customerTribe } = useGetCustomerTribe();

  useEffect(() => {
    if (customerTribe?.tribeId) {
      // If the customer already has a group, redirect to the group screen
      navigate(portalRoutes.friendsWithBenefits.groupOverview);
    }
  }, [customerTribe, navigate]);

  // Set info in sessionStorage to avoid showing the attention bubble a second time
  useEffect(
    () => sessionStorage.setItem("showFriendsWithBenefitsHint", "false"),
    [],
  );

  const { mutate: createTribe } = usePostTribe({
    mutation: {
      onSuccess: () => {
        setFullScreenLoading(false);
        // Navigate to group overview page
        navigate(portalRoutes.friendsWithBenefits.groupOverview);
      },
      onError: (error: ErrorType<unknown>) => {
        setFullScreenLoading(false);
        setIsSubmitting(false);

        if (
          error.response?.data ===
          '400 Bad Request: ""Could not create Tribe : payment type not allowed""'
        ) {
          showAlert({
            text: t("joinGroup.error.paymentMethod"),
            type: AlertTypes.error,
          });
        } else {
          handleError("Failed to create group", t("error.fail"));
        }
      },
    },
  });

  const handleCreateGroup = () => {
    setIsSubmitting(true);
    setFullScreenLoading(true, t("creatingGroup"));

    createTribe({ tribeName: t("groupName") });
  };

  return (
    <TabLayout header={<FriendsWithBenefitsHeader />}>
      <TwoColumnLayout
        rows={[
          {
            left: (
              <NewGroupCard
                dataTestid="fwb-new-group-card"
                customerName={customer?.firstName}
                isSubmitting={isSubmitting}
                onCreateGroup={handleCreateGroup}
              />
            ),
          },
        ]}
      />
    </TabLayout>
  );
};

export default CreateGroup;
